/**
 * Utility for fetching and caching Google Contact photos.
 * Uses the People API to get photos for contacts in the user's Google Contacts.
 */

const CACHE_KEY = "google_contact_photos";
const CACHE_EXPIRY_HOURS = 24;

interface PhotoCache {
  photos: Record<string, string>; // email -> photo URL
  timestamp: number;
}

/**
 * Get cached photos from localStorage
 */
function getPhotoCache(): Record<string, string> | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: PhotoCache = JSON.parse(cached);

    // Check if cache is expired
    const expiryMs = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
    if (Date.now() - data.timestamp > expiryMs) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data.photos;
  } catch {
    return null;
  }
}

/**
 * Save photos to localStorage cache
 */
function savePhotoCache(photos: Record<string, string>): void {
  try {
    const data: PhotoCache = {
      photos,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Silently fail if localStorage is full
  }
}

/**
 * Fetch all contacts with photos from Google People API
 */
export async function fetchGoogleContactPhotos(
  accessToken: string
): Promise<Record<string, string>> {
  // Check cache first
  const cached = getPhotoCache();
  if (cached) {
    return cached;
  }

  const photos: Record<string, string> = {};

  try {
    // Fetch contacts with email addresses and photos
    // Using connections.list which returns the user's contacts
    let nextPageToken: string | undefined;

    do {
      const url = new URL("https://people.googleapis.com/v1/people/me/connections");
      url.searchParams.set("personFields", "emailAddresses,photos");
      url.searchParams.set("pageSize", "1000");
      if (nextPageToken) {
        url.searchParams.set("pageToken", nextPageToken);
      }

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.warn("Failed to fetch Google contacts:", response.status);
        break;
      }

      const data = await response.json();

      // Process connections
      if (data.connections) {
        for (const person of data.connections) {
          // Get primary photo URL
          const photo =
            person.photos?.find(
              (p: { metadata?: { primary?: boolean }; url?: string }) => p.metadata?.primary
            )?.url || person.photos?.[0]?.url;

          if (!photo) continue;

          // Map all email addresses to this photo
          if (person.emailAddresses) {
            for (const emailObj of person.emailAddresses) {
              const email = emailObj.value?.toLowerCase().trim();
              if (email && !photos[email]) {
                photos[email] = photo;
              }
            }
          }
        }
      }

      nextPageToken = data.nextPageToken;
    } while (nextPageToken);

    // Cache the results
    savePhotoCache(photos);

    return photos;
  } catch (error) {
    console.warn("Error fetching Google contact photos:", error);
    return photos;
  }
}

/**
 * Clear the photo cache (useful when user logs out)
 */
export function clearGoogleContactPhotoCache(): void {
  localStorage.removeItem(CACHE_KEY);
}
